//* Importing react hooks
import React, { useState, useEffect, useCallback } from "react";

//* Importing react-router hooks
import { useParams, useNavigate } from "react-router-dom";

//*Importing components from react-bootstrap
import { Card, Alert, Button, Tabs, Tab } from "react-bootstrap";

//*Importing icons from lucide-react
import { AlertCircle, RefreshCcw } from "lucide-react";

//*Importing custom hooks from useUnits
import { useUnits } from "../../../hooks/units/useUnits";

//* Importing components
import { UnitHeader } from "../view/base/UnitHeader";
import { TeamsTab } from "../../../component/base/view/TeamsTab";
import { StudentsTab } from "../../../component/base/view/StudentsTab";
import { Loading } from "../../../component/common/Loading";

//*Importing type from Unit
import { type UnitDetails } from "../../../hooks/units/types/types";

export function UnitViewDetails(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [unitDetails, setUnitDetails] = useState<UnitDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem("token") || "";
  const { getUnitById } = useUnits(token);

  // Estados de filtro e paginação para estudantes
  const [studentFilters, setStudentFilters] = useState({
    searchTermStudents: "",
    studentFilter: "all" as "all" | "active" | "inactive",
    pageStudents: 1,
    perPageStudents: 10,
  });

  // Estados de filtro e paginação para turmas
  const [teamFilters, setTeamFilters] = useState({
    searchTermTeams: "",
    pageTeams: 1,
    perPageTeams: 10,
  });

  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState("teams"); // 'teams' ou 'students'

  const fetchUnitDetails = useCallback(async () => {
    if (token && id) {
      setLoading(true);
      setError(null);
      try {
        const details = await getUnitById(id, {
          ...studentFilters,
          ...teamFilters,
        });
        setUnitDetails(details);
      } catch (err) {
        console.error("Erro ao buscar detalhes da unidade:", err);
        setError(
          "Ocorreu um erro ao carregar os detalhes da unidade. Tente novamente."
        );
      } finally {
        setLoading(false);
      }
    }
  }, [id, getUnitById, token, studentFilters, teamFilters]);

  useEffect(() => {
    fetchUnitDetails();
  }, [fetchUnitDetails]);

  const handleBack = () => {
    navigate("/units");
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Card className="mb-4 shadow-sm">
      {/* Renderiza o cabeçalho apenas se os detalhes existirem e não houver erro */}
      {unitDetails && !error && <UnitHeader unitDetails={unitDetails} />}

      <Card.Body>
        {/* Renderiza o erro se existir */}
        {error && (
          <Alert
            variant="danger"
            className="d-flex align-items-center"
            role="alert"
          >
            <AlertCircle className="me-2" />
            <span>{error}</span>
            <Button
              variant="outline-danger" // Melhor semântica para botão de retry em alerta de erro
              size="sm"
              className="ms-auto"
              onClick={fetchUnitDetails}
              aria-label="Tentar novamente"
            >
              <RefreshCcw size={16} className="me-1" />
              Tentar Novamente
            </Button>
          </Alert>
        )}

        {/* Renderiza abas e conteúdo apenas se os detalhes existirem E não houver erro */}
        {unitDetails && !error && (
          <>
            <Tabs
              id="unit-details-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || "teams")}
              className="mb-3"
              fill
            >
              <Tab eventKey="teams" title="Turmas" aria-label="Turmas">
                <TeamsTab
                  teamsData={unitDetails.teams}
                  totalTeams={unitDetails.totalTeams}
                  teamFilters={teamFilters}
                  setTeamFilters={setTeamFilters}
                  reloadData={fetchUnitDetails}
                />
              </Tab>
              <Tab eventKey="students" title="Alunos" aria-label="Alunos">
                <StudentsTab
                  studentsData={unitDetails.students}
                  totalStudents={unitDetails.totalStudents}
                  totalActiveStudents={unitDetails.totalActiveStudents}
                  studentFilters={studentFilters}
                  setStudentFilters={setStudentFilters}
                  reloadData={fetchUnitDetails}
                />
              </Tab>
            </Tabs>
          </>
        )}

        {/* Renderiza o botão Voltar se houver detalhes OU se houver erro (permitir navegação) */}
        {(unitDetails || error) && (
           <div className="d-flex justify-content-end mt-4">
             <Button
               variant="secondary" // Usar secundário para ação de navegação
               onClick={handleBack}
               aria-label="Voltar para a lista de unidades"
             >
               Voltar
             </Button>
           </div>
        )}
      </Card.Body>
    </Card>
  );
}
